import { CAPABILITIES, GRANT_ONLY_CAPABILITIES, ROLES, ROLE_LABEL, api, type Capability, type Role } from '@agency/shared';
import { Button, Card, Checkbox, Chip, DataTable, Dialog, EmptyState, Field, Input, KeyValue, PageHeader, Skeleton, Tabs, Timeline, fmtTimestamp, type DataTableColumn, type TimelineItem } from '@agency/ui';
import { Link, useParams } from '@tanstack/react-router';
import { Ban, Check, Trash, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog, ErrorCallout, Grid, IdCell, Page, RoleChips, SensitiveButton, Stamp, UserLink } from '../../app/widgets.js';
import { DEVICE_STATUS_LABEL, DEVICE_STATUS_TONE, OUTCOME_TONE, USER_STATUS_TONE } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';

type Tab = 'profile' | 'scopes' | 'sessions' | 'devices' | 'history';

export function PersonDetailPage() {
  const { userId } = useParams({ strict: false }) as { userId: string };
  const session = useSession();
  const mutation = useMutation();
  const detail = useApi('admin.people.get', { params: { userId: userId as api.User['id'] } });
  const [tab, setTab] = useState<Tab>('profile');
  const canManage = session.has('people.manage');
  const user = detail.data?.user;
  // local-accounts change: accounts this person's devices report as present (read-only presence).
  const reportedAccountCount = detail.data?.accountsReported.length ?? 0;

  const [confirm, setConfirm] = useState<'suspend' | 'activate' | 'remove' | null>(null);

  const doLifecycle = async () => {
    if (!user) return;
    if (confirm === 'suspend') await mutation.run('admin.people.suspend', { params: { userId: user.id }, input: { revision: user.revision } }, { title: `${user.displayName} suspended`, invalidate: ['admin.people', 'admin.overview'] });
    if (confirm === 'activate') await mutation.run('admin.people.activate', { params: { userId: user.id }, input: { revision: user.revision } }, { title: `${user.displayName} activated`, invalidate: ['admin.people', 'admin.overview'] });
    if (confirm === 'remove') await mutation.run('admin.people.remove', { params: { userId: user.id }, input: { revision: user.revision, revokeDevices: true } }, { title: `${user.displayName} removed`, invalidate: ['admin.people', 'admin.overview', 'admin.devices'] });
    setConfirm(null);
  };

  return (
    <Page>
      <PageHeader
        eyebrow="People and access"
        eyebrowPath={`People / ${user?.displayName ?? '…'}`}
        title={user?.displayName ?? 'Person'}
        subtitle={user ? `${user.email} · ${user.status} · ${user.roles.length} roles · ${reportedAccountCount} accounts reported · ${detail.data?.devices.length ?? 0} devices · ${detail.data?.activeSessionCount ?? 0} active sessions` : 'Loading.'}
        actions={
          user && canManage ? (
            <>
              {user.status === 'active' ? <SensitiveButton variant="tertiary" icon={Ban} onClick={() => setConfirm('suspend')}>Suspend</SensitiveButton> : null}
              {user.status === 'suspended' ? <SensitiveButton variant="tertiary" icon={Check} onClick={() => setConfirm('activate')}>Activate</SensitiveButton> : null}
              {user.status !== 'removed' ? <SensitiveButton variant="primary" icon={Trash} onClick={() => setConfirm('remove')}>Remove access</SensitiveButton> : null}
            </>
          ) : undefined
        }
      />
      <ErrorCallout error={detail.error} onRetry={() => void detail.reload()} className="mt-6" />
      <div className="mt-6">
        <Tabs
          aria-label="Person sections"
          value={tab}
          onValueChange={(v) => setTab(v as Tab)}
          items={[
            { value: 'profile', label: 'Profile and roles' },
            { value: 'scopes', label: 'Accounts reported', count: reportedAccountCount },
            { value: 'sessions', label: 'Sessions', count: detail.data?.activeSessionCount ?? 0 },
            { value: 'devices', label: 'Devices', count: detail.data?.devices.length ?? 0 },
            { value: 'history', label: 'History' },
          ]}
        />
      </div>
      {!user ? (
        <Skeleton height={240} className="mt-6" />
      ) : tab === 'profile' ? (
        <ProfileTab detail={detail.data!} />
      ) : tab === 'scopes' ? (
        <AccountsReportedTab user={user} accountNames={detail.data!.accountNames} reported={detail.data!.accountsReported} />
      ) : tab === 'sessions' ? (
        <SessionsTab user={user} />
      ) : tab === 'devices' ? (
        <DevicesTab user={user} devices={detail.data!.devices} />
      ) : (
        <HistoryTab user={user} />
      )}
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm === 'suspend' ? 'Suspend access' : confirm === 'activate' ? 'Reactivate access' : 'Remove access'}
        subtitle={user?.displayName}
        confirmLabel={confirm === 'suspend' ? 'Suspend' : confirm === 'activate' ? 'Activate' : 'Remove'}
        busy={mutation.busy}
        onConfirm={doLifecycle}
        body={
          confirm === 'suspend'
            ? 'Sessions are revoked immediately. Entitlements and credential grants are denied until the user is reactivated.'
            : confirm === 'activate'
              ? 'The user can sign in again with their existing roles.'
              : 'Access is removed and registered devices are revoked. History is retained; the user record is kept for audit attribution.'
        }
      />
    </Page>
  );
}

function ProfileTab({ detail }: { detail: api.adminPeople.PersonDetailResponse }) {
  const session = useSession();
  const mutation = useMutation();
  const { user, invitation } = detail;
  const canManage = session.has('people.manage');
  const [roles, setRoles] = useState<Role[]>(user.roles);
  const [grants, setGrants] = useState<Capability[]>(user.capabilityGrants);
  useEffect(() => {
    setRoles(user.roles);
    setGrants(user.capabilityGrants);
  }, [user.roles, user.capabilityGrants, user.revision]);
  const rolesDirty = roles.length !== user.roles.length || roles.some((r) => !user.roles.includes(r));
  const grantsDirty = grants.length !== user.capabilityGrants.length || grants.some((g) => !user.capabilityGrants.includes(g));
  const grantable = CAPABILITIES.filter((c) => c === 'account.register' || GRANT_ONLY_CAPABILITIES.includes(c));
  const toggle = <T,>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  return (
    <Grid>
      <Card title="Identity" span="col-span-12 lg:col-span-5">
        <KeyValue
          items={[
            { key: 'User id', value: <IdCell value={user.id} short={false} tone="ink" /> },
            { key: 'Email', value: user.email, mono: true },
            { key: 'Status', value: <Chip tone={USER_STATUS_TONE[user.status]}>{user.status}</Chip> },
            { key: 'IdP subject', value: user.subject ?? 'not yet matched', mono: true, tone: user.subject ? undefined : 'mute' },
            { key: 'Created', value: fmtTimestamp(user.createdAt), mono: true },
            { key: 'Activated', value: user.activatedAt ? fmtTimestamp(user.activatedAt) : '--', mono: true },
            { key: 'Suspended', value: user.suspendedAt ? fmtTimestamp(user.suspendedAt) : '--', mono: true },
            { key: 'Last sign-in', value: user.lastSeenAt ? fmtTimestamp(user.lastSeenAt) : '--', mono: true },
            { key: 'Revision', value: String(user.revision), mono: true },
          ]}
        />
        {invitation ? (
          <div className="mt-4 border-t border-hair pt-4">
            <p className="label-mono">Invitation</p>
            <KeyValue
              className="mt-2"
              items={[
                { key: 'State', value: <Chip tone={invitation.status === 'pending' ? 'info' : 'mute'}>{invitation.status}</Chip> },
                { key: 'Expires', value: fmtTimestamp(invitation.expiresAt), mono: true },
                { key: 'Sent', value: `${invitation.lastSentAt ? fmtTimestamp(invitation.lastSentAt) : '--'} · ${invitation.resendCount} resends`, mono: true },
                { key: 'Delivery', value: invitation.deliveryState ?? '--', mono: true },
              ]}
            />
            {invitation.status === 'pending' && canManage ? (
              <div className="mt-3 flex gap-2">
                <Button variant="tertiary" size="sm" onClick={() => void mutation.run('admin.invitations.resend', { params: { invitationId: invitation.id }, input: {} }, { title: 'Invitation resent', invalidate: ['admin.people', 'admin.invitations'] })}>Resend</Button>
                <Button variant="tertiary" size="sm" onClick={() => void mutation.run('admin.invitations.cancel', { params: { invitationId: invitation.id }, input: {} }, { title: 'Invitation cancelled', invalidate: ['admin.people', 'admin.invitations'] })}>Cancel invitation</Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
      <Card
        title="Roles"
        subtitle="Presets · capabilities computed server-side"
        span="col-span-12 lg:col-span-7"
        rightSlot={canManage ? <SensitiveButton variant="primary" size="sm" disabled={!rolesDirty} loading={mutation.busy} onClick={() => void mutation.run('admin.people.setRoles', { params: { userId: user.id }, input: { roles, revision: user.revision } }, { title: 'Roles updated', invalidate: ['admin.people'] })}>Save roles</SensitiveButton> : undefined}
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ROLES.map((r) => (
            <Checkbox key={r} label={ROLE_LABEL[r]} checked={roles.includes(r)} disabled={!canManage} onCheckedChange={() => setRoles((prev) => toggle(prev, r))} />
          ))}
        </div>
        <div className="mt-5 border-t border-hair pt-4">
          <div className="flex items-center justify-between">
            <p className="label-mono">Explicit grants</p>
            {canManage ? <SensitiveButton variant="tertiary" size="sm" disabled={!grantsDirty} loading={mutation.busy} onClick={() => void mutation.run('admin.people.setCapabilityGrants', { params: { userId: user.id }, input: { grants, revision: user.revision } }, { title: 'Grants updated', invalidate: ['admin.people'] })}>Save grants</SensitiveButton> : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-3">
            {grantable.map((c) => (
              <Checkbox key={c} label={<span className="font-mono text-xs">{c}</span>} checked={grants.includes(c)} disabled={!canManage} onCheckedChange={() => setGrants((prev) => toggle(prev, c))} />
            ))}
          </div>
        </div>
        {/* real-providers change: the Direct send switch is gone; Agency creates Gmail drafts only. */}
        <div className="mt-5 border-t border-hair pt-4">
          <p className="label-mono">Effective capabilities</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {user.capabilities.length === 0 ? <span className="text-xs text-mute">--</span> : user.capabilities.map((c) => <Chip key={c} tone="mute"><span className="normal-case">{c}</span></Chip>)}
          </div>
        </div>
      </Card>
    </Grid>
  );
}

/**
 * local-accounts change: "Accounts reported" is read-only presence (what this person's devices hold);
 * expected accounts remain an optional, informational list that nothing enforces.
 */
function AccountsReportedTab({ user, accountNames, reported }: { user: api.User; accountNames: api.adminPeople.PersonDetailResponse['accountNames']; reported: api.adminPeople.PersonDetailResponse['accountsReported'] }) {
  const session = useSession();
  const mutation = useMutation();
  const canAssign = session.has('account.assign');
  const accounts = useApi('admin.accounts.list', { input: { limit: 200, lifecycle: 'active' } }, { enabled: session.has(['account.assign', 'account.register', 'audit.read']) });
  const [selected, setSelected] = useState<string[]>([...user.accountIds]);
  useEffect(() => setSelected([...user.accountIds]), [user.accountIds, user.revision]);
  const dirty = selected.length !== user.accountIds.length || selected.some((a) => !(user.accountIds as readonly string[]).includes(a));
  const list = accounts.data?.items ?? accountNames.map((a) => ({ id: a.id, displayName: a.displayName, provider: 'template_only' as const }));
  type ReportedRow = (typeof reported)[number];
  const reportedColumns = useMemo<DataTableColumn<ReportedRow>[]>(
    () => [
      { id: 'account', header: 'Account', cell: ({ row }) => <Link to="/accounts/$accountId" params={{ accountId: row.original.id }} className="text-ink hover:underline">{row.original.displayName}</Link>, size: 240 },
      { id: 'stableId', header: 'Stable id', cell: ({ row }) => <IdCell value={row.original.id} tone="brand" />, size: 110 },
      { id: 'devices', header: 'Devices', cell: ({ row }) => <span className="block text-right font-mono tabular-nums" title="This person's devices currently reporting the account">{row.original.deviceCount}</span>, size: 90 },
      { id: 'reported', header: 'Last reported', cell: ({ row }) => <span className="flex items-center gap-2"><Stamp value={row.original.lastReportedAt} relative /><span className="font-mono text-2xs uppercase text-dim">device reported</span></span>, size: 200 },
    ],
    [],
  );
  const deviceTotal = reported.reduce((n, r) => n + r.deviceCount, 0);
  return (
    <Grid>
      <Card title="Accounts reported" subtitle={`${reported.length} accounts present on this person's devices · ${deviceTotal} device reports · read-only`} span="col-span-12" flush>
        <DataTable<ReportedRow> aria-label={`Accounts reported by ${user.displayName}`} columns={reportedColumns} rows={reported} rowKey={(r) => r.id} height={Math.min(420, Math.max(160, reported.length * 34 + 40))} state={reported.length === 0 ? 'empty' : 'complete'} emptyMessage="No device of this person has reported an account yet." noun="accounts" />
        <div className="border-t border-hair px-5 py-3 text-xs text-mute">Accounts are added on the desktop; each device reports what it holds. Administrative access does not grant access to another person's local customer data.</div>
      </Card>
      <Card
        title="Expected accounts"
        subtitle={`${selected.length} of ${list.length} registry accounts · informational`}
        span="col-span-12 lg:col-span-8"
        rightSlot={canAssign ? <Button variant="tertiary" size="sm" disabled={!dirty} loading={mutation.busy} onClick={() => void mutation.run('admin.people.setAccountScopes', { params: { userId: user.id }, input: { accountIds: selected as api.User['accountIds'], revision: user.revision } }, { title: 'Expected accounts updated', invalidate: ['admin.people', 'admin.accounts'] })}>Save expected accounts</Button> : undefined}
      >
        {accounts.loading && !accounts.data ? (
          <Skeleton height={120} />
        ) : list.length === 0 ? (
          <EmptyState compact message="No registry accounts exist yet." />
        ) : (
          <ul className="divide-y divide-hair">
            {list.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-4 py-2">
                <Checkbox label={a.displayName} description={<span className="font-mono text-2xs">{a.id}</span>} checked={selected.includes(a.id)} disabled={!canAssign} onCheckedChange={() => setSelected((prev) => (prev.includes(a.id) ? prev.filter((x) => x !== a.id) : [...prev, a.id]))} />
                <Chip tone="mute">{a.provider === 'template_only' ? 'TEMPLATE' : a.provider}</Chip>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Note" span="col-span-12 lg:col-span-4">
        <p className="text-[13px] leading-relaxed text-ink/80">Expected accounts are a planning note for administrators. Nothing enforces them: operators add accounts on their own devices, and the table above shows what those devices report.</p>
      </Card>
    </Grid>
  );
}

function SessionsTab({ user }: { user: api.User }) {
  const session = useSession();
  const mutation = useMutation();
  const sessions = useApi('admin.people.sessions.list', { params: { userId: user.id } }, { enabled: session.has('people.manage') });
  const columns = useMemo<DataTableColumn<api.Session>[]>(
    () => [
      { id: 'kind', header: 'Kind', cell: ({ row }) => <Chip tone={row.original.kind === 'admin' ? 'info' : 'mute'}>{row.original.kind}</Chip>, size: 90 },
      { id: 'client', header: 'Client', cell: ({ row }) => <span>{row.original.clientSummary ?? '--'}</span>, size: 220 },
      { id: 'device', header: 'Device', cell: ({ row }) => <IdCell value={row.original.deviceId} />, size: 120 },
      { id: 'created', header: 'Created', cell: ({ row }) => <Stamp value={row.original.createdAt} />, size: 150 },
      { id: 'active', header: 'Last active', cell: ({ row }) => <Stamp value={row.original.lastActiveAt} relative />, size: 110 },
      { id: 'expires', header: 'Expires', cell: ({ row }) => <Stamp value={row.original.expiresAt} relative />, size: 110 },
      { id: 'state', header: 'State', cell: ({ row }) => (row.original.revokedAt ? <Chip tone="mute">revoked</Chip> : new Date(row.original.expiresAt).getTime() < Date.now() ? <Chip tone="mute">expired</Chip> : <Chip tone="ok">active</Chip>), size: 90 },
      {
        id: 'actions',
        header: '',
        size: 100,
        cell: ({ row }) =>
          row.original.revokedAt ? null : (
            <SensitiveButton size="sm" variant="tertiary" onClick={() => void mutation.run('admin.sessions.revoke', { params: { sessionId: row.original.id }, input: {} }, { title: 'Session revoked', invalidate: ['admin.people'] })}>Revoke</SensitiveButton>
          ),
      },
    ],
    [mutation],
  );
  const rows = sessions.data?.items ?? [];
  return (
    <div className="mt-6 rounded-sm border border-line bg-panel">
      <DataTable<api.Session> aria-label="Sessions" columns={columns} rows={rows} rowKey={(r) => r.id} height={Math.min(520, Math.max(200, rows.length * 34 + 40))} state={sessions.loading ? 'loading' : rows.length === 0 ? 'empty' : 'complete'} emptyMessage="No sessions recorded for this person." noun="sessions" />
    </div>
  );
}

function DevicesTab({ user, devices }: { user: api.User; devices: api.Device[] }) {
  const session = useSession();
  const mutation = useMutation();
  const canDevices = session.has('device.manage');
  const [updateFor, setUpdateFor] = useState<api.Device | null>(null);
  const [minVersion, setMinVersion] = useState('');
  const columns = useMemo<DataTableColumn<api.Device>[]>(
    () => [
      { id: 'name', header: 'Device', cell: ({ row }) => <span>{row.original.name}</span>, size: 180 },
      { id: 'id', header: 'Device id', cell: ({ row }) => <IdCell value={row.original.id} />, size: 120 },
      { id: 'platform', header: 'Platform', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.platform} · macOS {row.original.osVersion}</span>, size: 200 },
      { id: 'app', header: 'App', cell: ({ row }) => <span className="font-mono text-xs-plus">{row.original.appVersion}</span>, size: 100 },
      { id: 'status', header: 'Status', cell: ({ row }) => <Chip tone={DEVICE_STATUS_TONE[row.original.status]}>{DEVICE_STATUS_LABEL[row.original.status]}</Chip>, size: 110 },
      { id: 'ent', header: 'Entitlement', cell: ({ row }) => <Stamp value={row.original.lastEntitlementAt} relative />, size: 110 },
      { id: 'seq', header: 'Audit seq', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.lastAuditSequence ?? '--'}</span>, size: 90 },
      {
        id: 'actions',
        header: '',
        size: 220,
        cell: ({ row }) =>
          row.original.status === 'revoked' || !canDevices ? null : (
            <span className="flex gap-1">
              <Button size="sm" variant="tertiary" icon={Upload} onClick={() => { setUpdateFor(row.original); setMinVersion(''); }}>Require update</Button>
              <SensitiveButton size="sm" variant="tertiary" onClick={() => void mutation.run('admin.devices.revoke', { params: { deviceId: row.original.id }, input: {} }, { title: 'Device revoked', invalidate: ['admin.people', 'admin.devices'] })}>Revoke</SensitiveButton>
            </span>
          ),
      },
    ],
    [canDevices, mutation],
  );
  return (
    <div className="mt-6 rounded-sm border border-line bg-panel">
      <DataTable<api.Device> aria-label={`Devices of ${user.displayName}`} columns={columns} rows={devices} rowKey={(r) => r.id} height={Math.min(520, Math.max(200, devices.length * 34 + 40))} state={devices.length === 0 ? 'empty' : 'complete'} emptyMessage="No devices registered for this person." noun="devices" />
      <Dialog open={updateFor !== null} onOpenChange={(o) => !o && setUpdateFor(null)} title="Require update" subtitle={updateFor?.name} width={420} actions={<><Button variant="tertiary" onClick={() => setUpdateFor(null)}>Cancel</Button><Button variant="primary" disabled={!/^\d+\.\d+\.\d+/.test(minVersion)} loading={mutation.busy} onClick={async () => { if (!updateFor) return; const r = await mutation.run('admin.devices.requireUpdate', { params: { deviceId: updateFor.id }, input: { minAppVersion: minVersion } }, { title: 'Update required', invalidate: ['admin.people', 'admin.devices'] }); if (r) setUpdateFor(null); }}>Require</Button></>}>
        <Field label="Minimum app version" required help="The device must run at least this version before its next entitlement.">
          <Input mono value={minVersion} onChange={(e) => setMinVersion(e.target.value)} placeholder="1.0.0" />
        </Field>
      </Dialog>
    </div>
  );
}

function HistoryTab({ user }: { user: api.User }) {
  const session = useSession();
  const history = useApi('admin.people.history', { params: { userId: user.id }, input: { limit: 100 } }, { enabled: session.has(['people.manage', 'audit.read']) });
  const items = useMemo<TimelineItem[]>(
    () =>
      (history.data?.items ?? []).map((e) => ({
        id: e.eventId,
        meta: [fmtTimestamp(e.occurredAt), `evt ${e.eventId.slice(0, 8)}`],
        delta: e.outcome,
        deltaTone: e.outcome === 'success' ? 'ok' : e.outcome === 'denied' ? 'brand' : 'warn',
        nodeTone: e.outcome === 'success' ? 'ok' : e.outcome === 'denied' ? 'brand' : 'warn',
        title: <Link to="/audit/$eventId" params={{ eventId: e.eventId }} className="hover:underline">{e.action}</Link>,
        summary: (
          <span className="flex flex-wrap items-center gap-2">
            <span>by <UserLink userId={e.actorUserId} /></span>
            <Chip tone={OUTCOME_TONE[e.outcome]}>{e.outcome}</Chip>
            {e.details.reason ? <span className="font-mono text-2xs text-mute">{String(e.details.reason)}</span> : null}
          </span>
        ),
      })),
    [history.data],
  );
  return (
    <Grid>
      <Card title="History" subtitle={`${items.length} events · actor or target`} span="col-span-12 lg:col-span-8">
        <ErrorCallout error={history.error} onRetry={() => void history.reload()} />
        {history.loading ? <Skeleton height={200} /> : items.length === 0 ? <EmptyState compact message="No access changes recorded for this person." /> : <Timeline items={items} />}
      </Card>
      <Card title="Roles today" span="col-span-12 lg:col-span-4">
        <RoleChips roles={user.roles} />
      </Card>
    </Grid>
  );
}
